import React, { useState, useRef, useEffect } from 'react';
import './SwipeableMessage.css';

const SwipeableMessage = ({ children, onReply, disabled = false }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragDistance, setDragDistance] = useState(0);
  const [startX, setStartX] = useState(0);
  const [startY, setStartY] = useState(0);
  const [isVerticalScroll, setIsVerticalScroll] = useState(false);
  const messageRef = useRef(null);
  const animationRef = useRef(null);

  const REPLY_THRESHOLD = 80; // Distance needed to trigger reply
  const MAX_DRAG = 120; // Maximum drag distance

  // Reset drag state
  const resetDrag = () => {
    setDragDistance(0);
    setIsDragging(false);
    setIsVerticalScroll(false);
    if (messageRef.current) {
      messageRef.current.style.transform = 'translateX(0px)';
      messageRef.current.style.transition = 'transform 0.2s ease-out';
    }
  };

  // Handle drag movement
  const handleDrag = (clientX, clientY) => {
    if (disabled || isVerticalScroll) return;

    const deltaX = clientX - startX;
    const deltaY = Math.abs(clientY - startY);

    // If vertical movement is greater, treat as scroll
    if (!isDragging && deltaY > Math.abs(deltaX) && deltaY > 10) {
      setIsVerticalScroll(true);
      return;
    }

    // Only allow right swipe (positive deltaX)
    if (deltaX > 0) {
      const distance = Math.min(deltaX, MAX_DRAG);
      setDragDistance(distance);
      
      if (messageRef.current) {
        const opacity = Math.min(distance / REPLY_THRESHOLD, 1);
        messageRef.current.style.transform = `translateX(${distance}px)`;
        messageRef.current.style.transition = 'none';
        
        // Update reply icon opacity
        const replyIcon = messageRef.current.querySelector('.swipe-reply-icon');
        if (replyIcon) {
          replyIcon.style.opacity = opacity;
          replyIcon.style.transform = `scale(${0.5 + opacity * 0.5})`;
        }
      }
    }
  };

  // Mouse events
  const handleMouseDown = (e) => {
    if (disabled) return;
    setStartX(e.clientX);
    setStartY(e.clientY);
    setIsDragging(true);
    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (!isDragging || disabled) return;
    handleDrag(e.clientX, e.clientY);
  };

  const handleMouseUp = () => {
    if (!isDragging || disabled) return;
    
    if (dragDistance >= REPLY_THRESHOLD) {
      onReply();
    }
    
    resetDrag();
  };

  // Touch events
  const handleTouchStart = (e) => {
    if (disabled) return;
    const touch = e.touches[0];
    setStartX(touch.clientX);
    setStartY(touch.clientY);
    setIsDragging(true);
  };

  const handleTouchMove = (e) => {
    if (!isDragging || disabled) return;
    const touch = e.touches[0];
    handleDrag(touch.clientX, touch.clientY);
  };

  const handleTouchEnd = () => {
    if (!isDragging || disabled) return;
    
    if (dragDistance >= REPLY_THRESHOLD) {
      onReply();
    }
    
    resetDrag();
  };

  // Add global event listeners
  useEffect(() => {
    if (isDragging) {
      const handleGlobalMouseMove = (e) => handleMouseMove(e);
      const handleGlobalMouseUp = () => handleMouseUp();
      const handleGlobalTouchMove = (e) => {
        if (!isDragging) return;
        const touch = e.touches[0];
        handleDrag(touch.clientX, touch.clientY);
      };
      const handleGlobalTouchEnd = () => handleMouseUp();

      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);
      document.addEventListener('touchmove', handleGlobalTouchMove, { passive: false });
      document.addEventListener('touchend', handleGlobalTouchEnd);

      return () => {
        document.removeEventListener('mousemove', handleGlobalMouseMove);
        document.removeEventListener('mouseup', handleGlobalMouseUp);
        document.removeEventListener('touchmove', handleGlobalTouchMove);
        document.removeEventListener('touchend', handleGlobalTouchEnd);
      };
    }
  }, [isDragging, startX, startY, dragDistance]);

  return (
    <div className="swipeable-message-container">
      <div className="swipe-reply-background">
        <div className="swipe-reply-icon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path 
              d="M10 9V5l-7 7 7 7v-4.1c5 0 8.5 1.6 11 5.1-1-5-4-10-11-11z" 
              fill="currentColor"
            />
          </svg>
        </div>
      </div>
      <div
        ref={messageRef}
        className={`swipeable-message ${isDragging ? 'dragging' : ''}`}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {children}
      </div>
    </div>
  );
};

export default SwipeableMessage;